import type { ArticleCategory } from '../core/types/content'

/**
 * Gegenereerde sfeerbeelden per plek (docs/BEELDEN.md). Eén fotostijl: daglicht, linnen en zand,
 * warme huidtinten, geen tekst in beeld. Het zijn géén foto's van de echte salon of van Sarah:
 * tijdelijk, vervangen door eigen foto's vóór livegang. Een beeld dat in het beheer is gekozen,
 * gaat altijd voor; deze vullen alleen een plek die anders leeg zou zijn.
 */
export const placeholderImages = {
  /** H1 · Home, opening: huidspecialist met producten in de behandelkamer (4:5). */
  homeHero: 'https://cdn.magicpatterns.com/patterns/generated-images/d9ad7e80-ec5b-4241-933a-d199b3130144.jpg',
  /** W1 · Webshop: serum op de vingertop, een product in gebruik (21:9). */
  shopInUse: 'https://cdn.magicpatterns.com/patterns/generated-images/67641fd5-4c99-4ee8-aa9d-1f0d352cd9db.jpg',
  /** P2 · Product: textuur van crème en serum op zand (1:1). */
  productTexture: 'https://cdn.magicpatterns.com/patterns/generated-images/05943adc-8794-434e-bcce-7277493f8d06.jpg',
  /** S/O · Salon + Over + Home "kom langs": de behandelkamer in daglicht (4:5). */
  salonRoom: 'https://cdn.magicpatterns.com/patterns/generated-images/12880a60-c23b-49bb-b836-2609a96a26cb.jpg',
  /** C1 · Contact: de voordeur in daglicht (21:9). */
  contactDoor: 'https://cdn.magicpatterns.com/patterns/generated-images/abeab843-b3fc-4a51-a72d-80107c6c0aa5.jpg',
  /** S2 · Salon, bindweefselmassage: twee handen die een gezichtsmassage geven (4:5). Niet dezelfde foto als de opening. */
  massage: 'https://cdn.magicpatterns.com/patterns/generated-images/e7b2132e-c4c7-4f2e-9292-deb45e0f57e7.jpg',
} as const

/**
 * A2 · Een eigen tijdelijk beeld per artikel (slug), bij het onderwerp. Een beeld in de catalogus
 * gaat voor; een nieuw artikel zonder beeld valt terug op de textuur van zijn categorie.
 */
const articleImages: Record<string, string> = {
  'skincare-volgorde': 'https://cdn.magicpatterns.com/patterns/generated-images/947e9b3a-70ac-4caa-bf9c-1dbd6a482ca5.jpg',
  'emerginc-spf-50-tinted': 'https://cdn.magicpatterns.com/patterns/generated-images/d12c600c-fc41-4003-9769-47742e5656d4.jpg',
  'energetische-facelift': 'https://cdn.magicpatterns.com/patterns/generated-images/df5c5b58-8ddc-4e75-9da2-e60efe6459bb.jpg',
  'droge-huid-in-de-winter': 'https://cdn.magicpatterns.com/patterns/generated-images/5d988970-3e49-4e8f-8dd4-b06ba25f7dbe.jpg',
  'waarom-emerginc': 'https://cdn.magicpatterns.com/patterns/generated-images/eae589fe-d9dc-40ed-91bb-5e846f3dc055.jpg',
}

/** A1 · Artikel zonder eigen beeld: een rustige textuur (huid, ingrediënt of ritueel, 21:9). */
const articleTextures = {
  skin: 'https://cdn.magicpatterns.com/patterns/generated-images/8bcbf1b4-b9d3-475a-aa75-b830f15f2749.jpg',
  ingredient: 'https://cdn.magicpatterns.com/patterns/generated-images/02bb9398-6df2-4c83-9111-61437076fd2f.jpg',
  ritual: 'https://cdn.magicpatterns.com/patterns/generated-images/ff656fc9-cf09-43cb-a80a-5eb3b5062e1d.jpg',
} as const

const byCategory: Record<ArticleCategory, string> = {
  Producten: articleTextures.ingredient,
  Huidkennis: articleTextures.skin,
  'Energetisch werk': articleTextures.ritual,
}

/**
 * Het terugvalbeeld voor een artikel zonder eigen beeld: vast per categorie, zodat het journal
 * rustig blijft.
 */
export function articleTexture(category: ArticleCategory): string {
  return byCategory[category]
}

/**
 * Het beeld bij een artikel, overal hetzelfde (journal, artikel, "lees ook", home): het eigen
 * beeld, anders het tijdelijke beeld voor dit artikel, anders de textuur van de categorie.
 * Nooit leeg.
 */
export function articleImage(article: { slug: string; category: ArticleCategory; image?: string }): string {
  return article.image || articleImages[article.slug] || articleTexture(article.category)
}
